import React from 'react';

import {
  RecordContextProvider,
  useRecordContext,
} from 'react-admin';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { get, isEmpty } from 'lodash';

import FirstName from './FirstName';
import LastName from './LastName';
import TaxResidency from './TaxResidency';
import ControlType from './ControlType';
import FamilyControl from './FamilyControl';
import Citizenship from './Citizenship';
import Pep from './Pep';
import Custom from './Custom';

import languages, { Languages } from '../../../../constants/languages';
import Answer from '../Answer';
import DateOfBirth from './DateOfBirth';
import NationalIdentifier from './NationalIdentifier';
import Address from './Address';
import City from './City';
import PostalCode from './PostalCode';
import Residence from './Residence';
import conflictFields from '../../../../constants/conflictFields';
import questions from '../../../../constants/questions';
import { BeneficialOwnersParameters } from '../../questionParameters';

const LABEL = {
  [languages.EN]: 'Beneficial owners',
  [languages.SV]: 'Verklig huvudman',
  [languages.DE]: 'Wirtschaftlich Berechtigte/r',
};

const ValueField = ({ language, extraParameters }: {
  language?: Languages;
  extraParameters?: BeneficialOwnersParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value') as {firstName: string; lastName: string}[] | undefined;

  const sx = get(record, 'sx', {});

  if (isEmpty(value)) return null;

  return (
    <Card
      variant="outlined"
      square
      sx={{
        m: 0,
        py: 0,
        width: '100%',
      }}
    >
      <Grid container bgcolor="background.paper">
        {value?.map((beneficialOwner, index) => (
          <Grid
            key={`beneficialOwner-${index + 1}`}
            size={12}
          >
            <Accordion key={`${beneficialOwner.firstName}  ${beneficialOwner.lastName}`} sx={{ maxWidth: null, backgroundColor: 'transparent' }}>
              <AccordionSummary
                expandIcon={<ChevronRightIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                    ml: 4,
                    ...sx,
                  }}
                  variant="h6"
                >
                  {`${beneficialOwner.firstName} ${beneficialOwner.lastName}`}
                </Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <RecordContextProvider value={{ language, ...beneficialOwner, sx }}>
                  <Grid container spacing={6} py="1%" px="1%">
                    <FirstName.Nested extraParameters={extraParameters?.[questions.Q_FIRST_NAME]} />
                    <LastName.Nested extraParameters={extraParameters?.[questions.Q_LAST_NAME]} />
                    <TaxResidency.Nested
                      extraParameters={extraParameters?.[questions.Q_TAX_RESIDENCY]}
                    />
                    <DateOfBirth.Nested
                      extraParameters={extraParameters?.[questions.Q_DATE_OF_BIRTH]}
                    />
                    <Address.Nested extraParameters={extraParameters?.[questions.Q_ADDRESS]} />
                    <PostalCode.Nested
                      extraParameters={extraParameters?.[questions.Q_POSTAL_CODE]}
                    />
                    <City.Nested extraParameters={extraParameters?.[questions.Q_CITY]} />
                    <Residence.Nested extraParameters={extraParameters?.[questions.Q_RESIDENCE]} />
                    <NationalIdentifier.Nested
                      extraParameters={extraParameters?.[questions.Q_NATIONAL_IDENTIFIER]}
                    />
                    <Citizenship.Nested
                      extraParameters={extraParameters?.[questions.Q_CITIZENSHIP]}
                    />
                    <ControlType.Nested
                      extraParameters={extraParameters?.[questions.Q_CONTROL_TYPE]}
                    />
                    <FamilyControl.Nested
                      extraParameters={extraParameters?.[questions.Q_FAMILY_CONTROL]}
                    />
                    <Pep.Nested extraParameters={extraParameters?.[questions.Q_PEP]} />
                    {extraParameters?.customQuestions?.map((customQuestion) => (
                      <Custom.Nested key={customQuestion.id} extraParameters={customQuestion} />
                    ))}
                  </Grid>
                </RecordContextProvider>
              </AccordionDetails>
            </Accordion>
            {index + 1 !== value.length && <Divider />}
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

const BeneficialOwners = ({ extraParameters }: {
  extraParameters?: BeneficialOwnersParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  const hasBeneficialOwners = get(record, 'beneficialOwner') === 'yes';
  if (!hasBeneficialOwners) return null;

  return (
    <Answer
      label={extraParameters?.label ?? LABEL[language]}
      isComplexType
      datapoint={conflictFields.BENEFICIAL_OWNERS}
      identificationInfo={undefined}
      source="beneficialOwners"
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

BeneficialOwners.ValueField = ValueField;

export default BeneficialOwners;
